import { action, makeObservable, observable } from 'mobx';

import type { AlertsStore } from '@/configs/store/AlertsStore';

const STORAGE_KEY_SIDEBAR = 'timelog:calendar:sidebar-collapsed';
const STORAGE_KEY_OTHER_LOGS = 'timelog:calendar:show-other-logs';
const STORAGE_KEY_SHOW_DRAFTS = 'timelog:calendar:show-drafts';

export class CalendarPreferencesModel {
  sidebarCollapsed = false;
  showOtherLogs = true;
  showDrafts = true;

  private readonly _alertsStore: AlertsStore | null;

  constructor(alertsStore: AlertsStore) {
    this._alertsStore = alertsStore;
    this._loadFromStorage();

    makeObservable(this, {
      sidebarCollapsed: observable,
      showOtherLogs: observable,
      showDrafts: observable,
      toggleSidebar: action.bound,
      toggleOtherLogs: action.bound,
      toggleShowDrafts: action.bound,
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this._saveSidebarToStorage();
  }

  toggleOtherLogs(): void {
    this.showOtherLogs = !this.showOtherLogs;
    this._saveOtherLogsToStorage();
  }

  toggleShowDrafts(): void {
    this.showDrafts = !this.showDrafts;
    this._saveShowDraftsToStorage();
  }

  private _loadFromStorage(): void {
    this._loadSidebarFromStorage();
    this._loadOtherLogsFromStorage();
    this._loadShowDraftsFromStorage();
  }

  private _loadSidebarFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SIDEBAR);

      if (stored !== null) {
        this.sidebarCollapsed = JSON.parse(stored) === true;
      }
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _saveSidebarToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY_SIDEBAR, JSON.stringify(this.sidebarCollapsed));
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _loadOtherLogsFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_OTHER_LOGS);

      if (stored !== null) {
        this.showOtherLogs = JSON.parse(stored) !== false;
      }
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _saveOtherLogsToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY_OTHER_LOGS, JSON.stringify(this.showOtherLogs));
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _loadShowDraftsFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SHOW_DRAFTS);

      if (stored !== null) {
        this.showDrafts = JSON.parse(stored) !== false;
      }
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _saveShowDraftsToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_DRAFTS, JSON.stringify(this.showDrafts));
    } catch (error) {
      this._handleStorageError(error);
    }
  }

  private _handleStorageError(error: unknown): void {
    console.error('Ошибка работы с localStorage:', error);

    this._alertsStore?.addAlert({
      message: 'Не удалось сохранить настройки',
      autoClose: true,
      withReload: false,
    });
  }
}
